<template>
  <div
    v-if="series.length > 0 && periodKeys.length > 0"
    class="monthly-report-graph"
    data-testid="report-monthly-graph"
  >
    <div class="chart-wrapper">
      <div class="y-axis-labels">
        <div
          v-for="(label, index) in yAxisLabels"
          :key="'y-label-' + index"
          class="y-axis-label"
        >
          {{ label }}
        </div>
      </div>
      <div class="sparkline-container">
        <svg
          :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
          class="chart-svg"
          preserveAspectRatio="none"
        >
          <polyline
            v-for="item in visibleSeries"
            :key="item.id"
            :points="item.path"
            fill="none"
            :stroke="item.color"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <div class="mt-4 d-flex justify-center flex-wrap" data-testid="report-graph-legend">
      <v-chip
        v-for="item in series"
        :key="'chip-' + item.id"
        :data-testid="'report-graph-chip-' + item.id"
        small
        :color="item.color"
        text-color="white"
        :class="['mr-2', 'mb-2', { 'report-graph-chip-hidden': !item.visible }]"
      >
        <v-icon
          :data-testid="'report-graph-chip-eye-' + item.id"
          size="x-small"
          class="report-graph-chip-eye mr-1"
          :data-visible="item.visible ? 'true' : 'false'"
          :aria-label="item.visible ? $t('reports.hide_series') : $t('reports.show_series')"
          @click.stop="toggleVisibility(item.id)"
        >
          {{ item.visible ? 'mdi-eye' : 'mdi-eye-off' }}
        </v-icon>
        {{ item.label }}
      </v-chip>
    </div>
  </div>
</template>

<script>
const SERIES_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#c2185b',
  '#0097a7',
  '#5d4037',
  '#455a64',
  '#e64a19',
  '#512da8',
  '#00796b',
  '#d32f2f',
];

export default {
  name: 'MonthlyReportGraph',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    periodKeys: {
      type: Array,
      default: () => [],
    },
    /** Map of row id -> array of numbers aligned with periodKeys */
    valuesByRow: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hiddenIds: [],
      chartWidth: 800,
      chartHeight: 280,
      chartPadding: 20,
    };
  },
  computed: {
    hiddenIdSet() {
      return new Set(this.hiddenIds);
    },
    series() {
      return (this.rows || []).map((row, index) => {
        const values = (this.valuesByRow && this.valuesByRow[row.id]) || [];
        return {
          id: row.id,
          label: row.label || row.id,
          color: SERIES_COLORS[index % SERIES_COLORS.length],
          values,
          visible: !this.hiddenIdSet.has(row.id),
        };
      });
    },
    maxValue() {
      const allValues = [];
      this.series.forEach((item) => {
        if (!item.visible) {
          return;
        }
        (item.values || []).forEach((v) => {
          if (v != null && Number.isFinite(Number(v))) {
            allValues.push(Number(v));
          }
        });
      });
      const max = allValues.length ? Math.max(...allValues, 0) : 0;
      return Math.ceil(max / 10) * 10 || 10;
    },
    visibleSeries() {
      return this.series
        .filter((item) => item.visible)
        .map((item) => ({
          ...item,
          path: this.calculatePath(item.values),
        }));
    },
    yAxisLabels() {
      const labels = [];
      const steps = 5;
      for (let i = steps; i >= 0; i -= 1) {
        labels.push(Math.round((this.maxValue * i) / steps));
      }
      return labels;
    },
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        this.pruneHidden();
      },
    },
  },
  methods: {
    pruneHidden() {
      const ids = new Set((this.rows || []).map((r) => r.id));
      this.hiddenIds = this.hiddenIds.filter((id) => ids.has(id));
    },
    toggleVisibility(id) {
      if (this.hiddenIdSet.has(id)) {
        this.hiddenIds = this.hiddenIds.filter((x) => x !== id);
      } else {
        this.hiddenIds = [...this.hiddenIds, id];
      }
    },
    calculatePath(values) {
      if (!values || values.length === 0) {
        return '';
      }
      const width = this.chartWidth - this.chartPadding * 2;
      const height = this.chartHeight - this.chartPadding * 2;
      const maxVal = this.maxValue || 1;
      return values
        .map((value, index) => {
          const xRatio = values.length === 1 ? 0.5 : index / (values.length - 1);
          const x = this.chartPadding + xRatio * width;
          const num = value == null || !Number.isFinite(Number(value)) ? 0 : Number(value);
          const y = this.chartPadding + height - (num / maxVal) * height;
          return `${x},${y}`;
        })
        .join(' ');
    },
  },
};
</script>

<style scoped>
.monthly-report-graph {
  margin-top: 8px;
  margin-bottom: 16px;
}

.chart-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.y-axis-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
  min-width: 40px;
  text-align: right;
  font-size: 12px;
  color: #666;
}

.y-axis-label {
  height: 0;
  line-height: 1;
}

.sparkline-container {
  position: relative;
  height: 280px;
  width: 100%;
  min-height: 280px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-svg {
  width: 100%;
  height: 100%;
  max-width: 100%;
}

.report-graph-chip-eye {
  cursor: pointer;
}

.report-graph-chip-hidden {
  opacity: 0.55;
}
</style>
